import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import type { ForumBase } from '../types/forum';
import { formatChangePercent } from './forumStatChange';
import { lastUpdatedSource } from './formatLastUpdated';

export interface CategoryAggregate {
  forumCount: number;
  totalMembers: number;
  previousTotalMembers: number;
  totalPosts: number;
  previousTotalPosts: number;
  totalTopics: number;
  previousTotalTopics: number;
}

export function aggregateCategoryForums(forums: ForumBase[]): CategoryAggregate {
  return {
    forumCount: forums.length,
    totalMembers: forums.reduce((s, f) => s + (f.members_count ?? 0), 0),
    previousTotalMembers: forums.reduce((s, f) => s + (f.previous_month_members_count ?? 0), 0),
    totalPosts: forums.reduce((s, f) => s + (f.posts_count ?? 0), 0),
    previousTotalPosts: forums.reduce((s, f) => s + (f.previous_month_posts_count ?? 0), 0),
    totalTopics: forums.reduce((s, f) => s + (f.topics_count ?? 0), 0),
    previousTotalTopics: forums.reduce((s, f) => s + (f.previous_month_topics_count ?? 0), 0),
  };
}

export function latestForumUpdateIso(forums: ForumBase[]): string | null {
  let latest: number | null = null;
  for (const f of forums) {
    const src = lastUpdatedSource(f);
    if (!src) continue;
    const t = new Date(src).getTime();
    if (Number.isNaN(t)) continue;
    if (latest === null || t > latest) latest = t;
  }
  return latest !== null ? new Date(latest).toISOString() : null;
}

export function formatCategoryLastUpdate(iso: string | null): string {
  if (!iso) return 'Henüz güncellenmedi';
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return 'Henüz güncellenmedi';
    return format(date, "d MMMM yyyy, HH:mm", { locale: tr });
  } catch {
    return 'Henüz güncellenmedi';
  }
}

function formatCount(n: number): string {
  return n.toLocaleString('tr-TR');
}

function growthSentence(label: string, current: number, previous: number): string {
  const pct = formatChangePercent(current, previous);
  if (!pct) return `${label} için önceki dönem karşılaştırması henüz yok`;
  const diff = current - previous;
  const direction = diff >= 0 ? 'artış' : 'azalış';
  return `${label} geçen döneme göre ${pct} ${direction} (${formatCount(Math.abs(diff))} ${diff >= 0 ? 'fazla' : 'eksik'})`;
}

/** Tek kategori sayfası ve meta açıklaması için doğal dil özeti */
export function buildCategorySeoParagraph(
  categoryName: string,
  agg: CategoryAggregate,
  lastUpdateIso: string | null,
  categoryDescription?: string | null
): string {
  const intro =
    categoryDescription?.trim() ||
    `${categoryName} kategorisindeki Türk forum sitelerinin güncel üye, konu ve ileti istatistikleri.`;

  const lastPart = lastUpdateIso
    ? `Veriler en son ${formatCategoryLastUpdate(lastUpdateIso)} tarihinde güncellendi.`
    : 'Veri güncelleme tarihi henüz kayıtlı değil.';

  const totals = `${categoryName} kategorisinde ${agg.forumCount} aktif forum bulunuyor; toplam ${formatCount(agg.totalMembers)} üye, ${formatCount(agg.totalTopics)} konu ve ${formatCount(agg.totalPosts)} ileti kayıtlı.`;

  const growth = [
    growthSentence('Üye sayısı', agg.totalMembers, agg.previousTotalMembers),
    growthSentence('Konu sayısı', agg.totalTopics, agg.previousTotalTopics),
    growthSentence('İleti sayısı', agg.totalPosts, agg.previousTotalPosts),
  ].join('. ');

  return `${intro} ${totals} ${lastPart} ${growth}.`;
}

/** Tüm kategoriler listesi sayfası için genel özet */
export function buildCategoriesListSeoParagraph(
  categoryCount: number,
  agg: CategoryAggregate,
  lastUpdateIso: string | null
): string {
  const lastPart = lastUpdateIso
    ? `Son veri güncellemesi: ${formatCategoryLastUpdate(lastUpdateIso)}.`
    : '';

  const totals = `Sitemizde ${categoryCount} forum kategorisi ve toplam ${formatCount(agg.forumCount)} aktif forum listeleniyor; bir arada ${formatCount(agg.totalMembers)} üye, ${formatCount(agg.totalTopics)} konu ve ${formatCount(agg.totalPosts)} ileti istatistiği tutuluyor.`;

  const growth = [
    growthSentence('Toplam üye', agg.totalMembers, agg.previousTotalMembers),
    growthSentence('Toplam konu', agg.totalTopics, agg.previousTotalTopics),
    growthSentence('Toplam ileti', agg.totalPosts, agg.previousTotalPosts),
  ].join('. ');

  return `Türk forum sitelerinin kategorilere göre sınıflandırılmış istatistik rehberi. ${totals} ${lastPart} ${growth}.`;
}

export function categoryMemberGrowthPercent(agg: CategoryAggregate): number | null {
  const prev = agg.previousTotalMembers;
  if (prev <= 0) return null;
  return ((agg.totalMembers - prev) / prev) * 100;
}
